import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE from "../config/api";
import { auth } from "../config/config-firebase";
import { isPromoLoginMode } from "../utils/adminMerchantMode";

export const ACCESS_DISABLED_MESSAGE =
  "Your access has been disabled by the store owner. Please contact them for help.";

// ---------------------------------------------------------------
// Write payloads are confirmed from the Postman collection:
//   Save Store            -> { storeName, storeLocation, userId }
//   Update Store Details  -> { storeId, storeName, storeLocation }  (no userId)
//   Delete Store          -> { storeId, userId }
//   Change Default Store  -> { storeId, userId }
//   Get Merchant Stores   -> { userId }
//
// IMPORTANT: `userId` in every one of these payloads must be the merchant's
// numeric account_user.id (e.g. 243) — NEVER the Firebase auth uid (a
// string like "ONJVEYlgenWfBmM3vu6iz3nkuWm2"). The backend writes whatever
// is sent straight into store_user.account_user_id with no lookup or
// validation, so sending the wrong value here silently corrupts data
// (confirmed via Postman: userId 243 returns real stores, the Firebase
// uid string returns { success: true, data: null }).
//
// `numericId`/`numericUserId` is sourced from primaryData.id, fetched via
// merchantDetails/{uid}. Backend's getMerchantDetails selects `store.*`
// first and `au.id` after it in the same query — since both sides have an
// `id` column, the later one (au.id, the real account_user.id) wins in the
// resulting row, so `.id` here is already account_user.id, not store.id.
// (Was previously store.id until the backend query was fixed to select
// au.id after store.* — see Backend/database/mysqlDB.js getMerchantDetails.)
// ---------------------------------------------------------------

// An explicit `storeId: null` (set below for a primary row that has no
// store_user link — see the "legacy account" branch further down) means
// "known unavailable", not "fall through to `.id`". Without this check,
// `row.storeId ?? row.id` would silently fall through to `.id` anyway (`??`
// only short-circuits on the LEFT side being nullish, and `.id` — the
// merchant's account_user.id, see the note above — is never null), handing
// callers the wrong id to write with.
export const getStoreId = (row) =>
  Object.prototype.hasOwnProperty.call(row, "storeId") ? row.storeId : (row.id ?? row.store_id);

export const formatStoreAddress = (row) =>
  [row.address_line, row.town_city, row.postcode].filter(Boolean).join(", ");

export const isDefaultStoreRow = (row) => Number(row.is_default_store ?? row.default) === 1;

// Fired on window right after a successful default-store switch, once the
// new store's customer_id has been written to localStorage. Screens that
// load customer-scoped data (menu, modes, schedules, ...) and are already
// mounted when the switch happens can listen for this to refetch in place —
// otherwise they'd only pick up the new customer_id on their next mount.
export const ACTIVE_CUSTOMER_ID_CHANGED_EVENT = "activeCustomerIdChanged";

// Resolve the DEFAULT store's customer_id for a Firebase uid, without
// mounting the full hook. For imperative flows (device_login linking in
// Home.js/DeviceLogin.js) that need the value immediately, not after a
// hook's async state settles and the component re-renders.
//
// merchantDetails/:uid alone isn't enough here: its backend query has no
// ORDER BY, so for a merchant with multiple stores it returns whichever
// store row the join happens to return first — not necessarily the default
// one. This mirrors useMerchantStores' own default-store selection instead.
export async function resolveDefaultStoreCustomerId(uid) {
  if (!uid) return null;
  try {
    const primaryRes = await fetch(`${API_BASE}/merchantDetails/${uid}`);
    const primaryJson = primaryRes.ok ? await primaryRes.json().catch(() => null) : null;
    const primaryData = primaryJson?.success && primaryJson?.data ? primaryJson.data : null;

    const numericId = primaryData?.id ?? null;
    if (!numericId) return primaryData?.customer_id ?? null;

    const res = await fetch(`${API_BASE}/getMerchantStoreDetails`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: Number(numericId) }),
    });
    if (!res.ok) return primaryData?.customer_id ?? null;

    const json = await res.json();
    const rows = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];

    const defaultRow = rows.find((row) => isDefaultStoreRow(row));
    return defaultRow?.customer_id ?? primaryData?.customer_id ?? rows[0]?.customer_id ?? null;
  } catch (error) {
    console.error("Error resolving default store customer_id:", error);
    return null;
  }
}

// Shared by StoreListPage (Settings -> My Stores) and the top-bar
// StoreSwitcher (Mode / Schedule / API Menu) so the fetch + merge + switch
// logic never drifts between the two call sites.
export default function useMerchantStores(uid) {
  const navigate = useNavigate();
  const [numericUserId, setNumericUserId] = useState(null);
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(true);
  const [switchingId, setSwitchingId] = useState(null);
  const [error, setError] = useState("");
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [isTeamMemberActive, setIsTeamMemberActive] = useState(true);

  const fetchStores = useCallback(async () => {
    if (!uid) {
      setStores([]);
      setNumericUserId(null);
      setIsTeamMember(false);
      setIsTeamMemberActive(true);
      setLoadingStores(false);
      return;
    }
    setLoadingStores(true);
    try {
      // Promo Login Mode (PromoLogin.js): `uid` here is actually the active
      // customer_id, not a Firebase uid — merchantDetails/:uid would never
      // match anything and always 404. A promo session already has the
      // account's real account_user.id cached (PromoLogin sets
      // localStorage.userId from /promoLogin's response), so skip the
      // Firebase-uid lookup entirely and resolve numericId from that instead.
      const promoMode = isPromoLoginMode();
      const promoUserId = promoMode && localStorage.getItem("userId")
        ? Number(localStorage.getItem("userId"))
        : null;

      // The merchant's own primary store (linked directly via customer_id,
      // not through the store_user table used by getMerchantStoreDetails).
      const primaryJson = promoMode
        ? null
        : await fetch(`${API_BASE}/merchantDetails/${uid}`)
            .then((res) => (res.ok ? res.json().catch(() => null) : null));
      const primaryData = primaryJson?.success && primaryJson?.data ? primaryJson.data : null;
      const primaryRow = primaryData
        ? { ...primaryData, address: formatStoreAddress(primaryData) }
        : null;

      // Re-checked fresh from the API on every load — a disabled member is
      // cut off immediately here, not just at their next OTP login. This is
      // the shared gate for every screen that reads store data through this
      // hook (Mode, Settings, StoreListPage, TeamMembers, AI Menu, Schedule
      // Hub, StoreSwitcher), so a stale "customer_id" left over in
      // localStorage from before they were disabled can't keep them in.
      // Doesn't apply in Promo Login Mode — promoLogin's own 403 check
      // (mirroring merchantUserDetails) already gates this at login time.
      const memberIsTeamMember = Number(primaryData?.is_teammember) === 1;
      const memberIsActive = Number(primaryData?.is_teammember_active) !== 0;
      if (memberIsTeamMember && !memberIsActive) {
        setIsTeamMember(true);
        setIsTeamMemberActive(false);
        setStores([]);
        setNumericUserId(null);
        setLoadingStores(false);
        try {
          await auth.signOut();
        } catch (signOutErr) {
          console.error("Error signing out disabled team member:", signOutErr);
        }
        localStorage.removeItem("customer_id");
        localStorage.removeItem("phone");
        navigate("/login", { state: { disabledMessage: ACCESS_DISABLED_MESSAGE } });
        return;
      }

      // The numeric id every write/read endpoint expects as `userId` — see
      // the note at the top of this file: merchantDetails/{uid} returns
      // account_user.id as `.id` (not store.id). Promo sessions skip that
      // lookup entirely and use the cached promoUserId instead (above).
      const numericId = promoMode ? promoUserId : (primaryData?.id ?? null);
      setNumericUserId(numericId);

      // Additional stores this user has added/linked (multi-store feature),
      // resolved from the merchant's real account_user.id via merchantDetails
      // above — this is what ties "who is this user" to "which stores do
      // they have".
      const res = await fetch(`${API_BASE}/getMerchantStoreDetails`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: Number(numericId) }),
      });

      if (!res.ok) throw new Error("Failed to load stores.");

      const json = await res.json();

      // Defensive: accept { data: [...] } or a bare array response.
      const rows = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
      const mappedRows = rows.map((row) => ({ ...row, address: formatStoreAddress(row) }));

      // merchantDetails/:uid now exposes account_user.is_teammember /
      // is_teammember_active directly, so use those instead of the old
      // "exactly one linked store" proxy (which false-positived on any
      // owner who had only ever created a single store).
      setIsTeamMember(memberIsTeamMember);
      setIsTeamMemberActive(memberIsActive);

      // If the primary store (from merchantDetails) is ALSO present in the
      // store_user-linked list — matched by name/address — reuse that real
      // row instead of building a separate card from primaryData.
      const primaryMatchIndex = primaryRow
        ? mappedRows.findIndex(
            (row) =>
              row.full_name === primaryRow.full_name &&
              row.address_line === primaryRow.address_line
          )
        : -1;

      let orderedStores;
      if (primaryMatchIndex !== -1) {
        const rest = mappedRows.filter((_, idx) => idx !== primaryMatchIndex);
        orderedStores = [mappedRows[primaryMatchIndex], ...rest];
      } else if (primaryRow) {
        // Legacy account: this primary store has no store_user row yet,
        // so there's no real storeId for it. Show it, but storeId stays
        // null — edit/delete/switch-default are guarded off for this
        // row elsewhere (getStoreId returns null, callers check for it).
        orderedStores = [{ ...primaryRow, storeId: null }, ...mappedRows];
      } else {
        orderedStores = mappedRows;
      }

      // Guard against more than one row claiming default. First claim
      // wins; if none claim it, the first row wins by default.
      let defaultAssigned = false;
      const finalStores = orderedStores.map((row) => {
        const rowIsDefault = !defaultAssigned && Number(row.is_default_store) === 1;
        if (rowIsDefault) defaultAssigned = true;
        return { ...row, is_default_store: rowIsDefault ? 1 : 0 };
      });
      if (!defaultAssigned && finalStores.length > 0) {
        finalStores[0] = { ...finalStores[0], is_default_store: 1 };
      }

      setStores(finalStores);
    } catch (err) {
      console.error("Failed to fetch merchant store details:", err);
      setError("Could not load your stores.");
      setStores([]);
      setIsTeamMember(false);
      setIsTeamMemberActive(true);
    } finally {
      setLoadingStores(false);
    }
  }, [uid]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  const handleSetDefault = useCallback(
    async (store) => {
      const storeId = getStoreId(store);
      if (!storeId) {
        setError("This store isn't linked yet and can't be set as default. Contact support.");
        return false;
      }
      if (!numericUserId) {
        setError("Still loading your account details — try again in a moment.");
        return false;
      }

      setSwitchingId(storeId);
      setError("");
      try {
        const res = await fetch(`${API_BASE}/changeMerchantDefaultStore`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ storeId: Number(storeId), userId: Number(numericUserId) }),
        });

        const json = await res.json().catch(() => ({}));

        if (!res.ok || json.success === false) {
          throw new Error(json.message || "Failed to switch default store.");
        }

        // Each store has its own customer_id (see getMerchantStoreDetails /
        // merchantDetails), which scopes the AI menu, modes, schedules, etc.
        // Keep it in sync with the newly-active store so those screens read
        // the right store's data on their next fetch.
        if (store.customer_id) {
          localStorage.setItem("customer_id", store.customer_id);
          window.dispatchEvent(
            new CustomEvent(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, {
              detail: { customerId: store.customer_id },
            })
          );
        }

        await fetchStores();
        return true;
      } catch (err) {
        console.error("Error switching default store:", err);
        setError(err.message || "Failed to switch default store.");
        return false;
      } finally {
        setSwitchingId(null);
      }
    },
    [numericUserId, fetchStores]
  );

  return {
    stores,
    loadingStores,
    numericUserId,
    switchingId,
    error,
    setError,
    fetchStores,
    handleSetDefault,
    isTeamMember,
    isTeamMemberActive,
  };
}
